import React from "react";
import i18n from "../../../../i18n/i18n";
import {AxiosResponse} from "axios";
import {HttpErrorModel} from "../../../../common/models/httpError";
import {RouteComponentProps, withRouter} from "react-router";
import {Api} from "../../../../helpers/api";
import {HttpError} from "../../../../common/components/shared/httpError/httpError";
import Loader from "../../../../common/components/shared/loader/loader";
import {RouteParamsModel} from "../../../../common/models/routeParams";
import SimpleInfoElement from "../../../../common/components/shared/simpleInfoElement/simpleInfoElement";
import Table from "../../../../common/components/shared/table/table";
import {OrderDetailModel} from "../../../models/order";
import {getContainerSize} from "../../../../helpers/containerSize";
import {Utils} from "../../../../helpers/utils";
import ReloadButton from "../../../../common/components/shared/reloadButton/reloadButton";
import {OrderService} from "../../../services/orderService";
import TransportationActions from "../../transportationActions/transportationActions";
import {storesContext} from "../../../../common/stores/stores";
import {IUserStore} from "../../../../administration/stores/userStore";
import {NavLink} from "react-router-dom";
import {routingAddresses} from "../../../../helpers/routing";
import Button from "../../../../common/components/shared/button/button";
import {ReactComponent as EditIcon} from "../../../../statics/icons/icon_edit.svg";
import PrintFileButton from "./printFileButton/printFileButton";
import {ReactComponent as AllowIcon} from "../../../../statics/icons/icon_allow.svg";
import {ReactComponent as NotAllowIcon} from "../../../../statics/icons/icon_discard.svg";
import AllowOrNotAllowPopup from "../../../../common/components/shared/allowOrNotAllowPopup/allowOrNotAllowPopup";
import {ValidatedValue} from "../../../../helpers/validatedValue";
import Validator from "../../../../helpers/validator";
import {errorMessages} from "../../../../helpers/errorMessage";
import {observer} from "mobx-react";
import {IOrderStore} from "../../../stores/orderStore";
import TransportUtils from "../../../../helpers/transportUtils";
import {OrderUtils} from "../../../../helpers/orderUtils";
import AssignWagonsNumberPopup from "./assignWagonsNumberPopup/assignWagonsNumberPopup";
import {RelationType} from "../../../models/relation";

interface OrderDetailsState {
    orderData?: OrderDetailModel | null;
    response: AxiosResponse<HttpErrorModel[] | any> | undefined;
    id: string | null;
    displayPopup: "allowPopup" | "notAllowPopup" | "assignWagonsNumberPopup" | null;
    allowResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    notAllowResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    assignWagonsNumberResponse: AxiosResponse<HttpErrorModel[] | any> | undefined;
    selectedLocation: ValidatedValue<string>;
    isPopupActionWaiting: boolean;
    wagonRegNo: ValidatedValue<string>;
    supplierWagonRegNo: ValidatedValue<string>;
    recipientWagonRegNo: ValidatedValue<string>;
}

const notEmptyValidator = {
    name: "valueIsNotEmpty",
    fn: Validator.isNotEmpty,
    errorMessage: errorMessages.required
};

class OrderDetails extends React.Component <RouteComponentProps, OrderDetailsState> {
    static contextType = storesContext;

    constructor(props: any) {
        super(props);

        this.state = {
            orderData: null,
            response: undefined,
            id: null,
            displayPopup: null,
            allowResponse: undefined,
            notAllowResponse: undefined,
            assignWagonsNumberResponse: undefined,
            selectedLocation: new ValidatedValue("", [notEmptyValidator]),
            isPopupActionWaiting: false,
            wagonRegNo: new ValidatedValue(""),
            supplierWagonRegNo: new ValidatedValue(""),
            recipientWagonRegNo: new ValidatedValue("")
        };
    }

    async componentDidMount() {
        const params = this.props.match.params as RouteParamsModel;
        const response = await OrderService.getOrder(params.id);
        const orderStore = this.context.orderStore as IOrderStore;

        orderStore.relationType.setValue(Api.isSuccess(response) ? response?.data.relationType : "");
        this.setState({
            orderData: Api.isSuccess(response) ? response?.data : null,
            response,
            id: params.id
        });

        this.addValidatorsForWagonsNumber(this.state.orderData);

        if (this.state.orderData) {
            this.state.wagonRegNo.setValue(this.state.orderData.wagonRegNo || "");
            this.state.supplierWagonRegNo.setValue(this.state.orderData.supplierWagonRegNo || "");
            this.state.recipientWagonRegNo.setValue(this.state.orderData.recipientWagonRegNo || "");
        }
    }

    public addValidatorsForWagonsNumber(orderData: OrderDetailModel | null | undefined) {
        const atLeastOneFilled = {
            name: "valuesAtLeastOneFilled",
            fn: () => Validator.atLeastOneFieldHasBeenCompleted([this.state.wagonRegNo, this.state.supplierWagonRegNo, this.state.recipientWagonRegNo]),
            errorMessage: () => `${i18n.t("errorMessages.atLeastOneFilled")}`
        };

        if (orderData && orderData.relationType) {
            switch (orderData.relationType) {
                case RelationType.RAIL_DEPO_FERRY:
                case RelationType.FERRY_DEPO_RAIL:
                case RelationType.RAIL_FERRY:
                case RelationType.FERRY_RAIL:
                    this.state.wagonRegNo.addValidators([notEmptyValidator]);
                    break;
                case RelationType.RAIL_DEPO_ROAD:
                case RelationType.RAIL_DEPO:
                    this.state.supplierWagonRegNo.addValidators([notEmptyValidator]);
                    break;
                case RelationType.RAIL_DEPO_RAIL:
                    if (!orderData.supplierWagonRegNo && !orderData.recipientWagonRegNo) {
                        this.state.supplierWagonRegNo.addValidators([atLeastOneFilled]);
                        this.state.recipientWagonRegNo.addValidators([atLeastOneFilled]);
                    } else if (!orderData.supplierWagonRegNo) {
                        this.state.supplierWagonRegNo.addValidators([notEmptyValidator]);
                    } else if (!orderData.recipientWagonRegNo) {
                        this.state.recipientWagonRegNo.addValidators([notEmptyValidator]);
                    }
                    break;
                case RelationType.ROAD_DEPO_RAIL:
                    this.state.recipientWagonRegNo.addValidators([notEmptyValidator]);
                    break;
                case RelationType.ROAD_DEPO:
                case RelationType.FERRY_DEPO:
                case RelationType.ROAD_DEPO_ROAD:
                case RelationType.FERRY_DEPO_ROAD:
                case RelationType.ROAD_DEPO_FERRY:
                default:
                    break;
            }
        }
    }

    public render(): JSX.Element {
        return (
            <div className="transportation-order-details">
                <div className="content transportation-order-content">
                    {this.renderContent()}
                    {(this.state.displayPopup === "allowPopup" || this.state.displayPopup === "notAllowPopup") && this.renderAllowOrNotAllowPopups()}
                    {this.state.displayPopup === "assignWagonsNumberPopup" && this.renderAssignWagonsNumberPopup()}
                </div>
            </div>
        );
    }

    private renderContent() {
        if (!Api.isSuccess(this.state.response)) {
            return <HttpError
                response={this.state.response}
                cause={i18n.t("transportation.order.preview.retrievalErrorSuffix")}
            />;
        } else if (this.state.orderData) {
            return this.renderDetails();
        } else {
            return (
                <Loader />
            );
        }
    }

    private renderDetails() {
        const userStore = this.context.userStore as IUserStore;

        const displayAssignWagonNumbersButton =  userStore.canAssigningWagonNumber && OrderUtils.displayAssignWagonNumbersButton(this.state.orderData!.relationType, this.state.orderData!);

        return (
            <>
                <TransportationActions
                    orderId={this.state.orderData?.id || ""}
                    element="order"
                    relationType={this.state.orderData!.relationType}
                />
                <div>
                    <div className="header" data-testid="header">
                        {i18n.t("transportation.order.preview.header", { number: this.state.orderData!.id })}
                        <div className="actions">
                            {displayAssignWagonNumbersButton && (
                                <Button
                                    className="assign-wagons-number-btn"
                                    text={i18n.t("assignWagonsNumberBtn")}
                                    type="secondary"
                                    variant="outlined"
                                    testId="assign-wagons-number-btn"
                                    onClick={() => {
                                        this.setState({displayPopup: "assignWagonsNumberPopup"});
                                    }}
                                />
                            )}
                            <PrintFileButton orderId={this.state.id} orderData={this.state.orderData} />
                            {userStore.displayKioskPassageDirection && (
                                <>
                                    <Button
                                        className="not-allow"
                                        type="primary"
                                        text={i18n.t("transportation.order.actions.notAllow")}
                                        endIcon={<NotAllowIcon />}
                                        onClick={() => {
                                            this.state.selectedLocation.clearValidation();
                                            this.onAllowOrNorAllowAction("notAllowPopup");
                                        }}
                                        testId="not-allow-btn"
                                    />
                                    <Button
                                        className="allow"
                                        type="primary"
                                        text={i18n.t("transportation.order.actions.allow")}
                                        endIcon={<AllowIcon />}
                                        onClick={() => {
                                            this.state.selectedLocation.clearValidation();
                                            this.onAllowOrNorAllowAction("allowPopup");
                                        }}
                                        testId="allow-btn"
                                    />
                                </>
                            )}
                            {userStore.canModifyOrder && (
                                <NavLink
                                    to={`${routingAddresses.order.modify}/${this.state.id}`}
                                    data-testid="modify-order"
                                    className="action-button"
                                >
                                    <Button
                                        type="primary"
                                        text={i18n.t("transportation.order.actions.modifyOrder")}
                                        endIcon={<EditIcon />}
                                    />
                                </NavLink>
                            )}
                            <ReloadButton
                                reloadFunc={this.reload.bind(this)}
                                testId="order-details-reload-btn"
                            />
                        </div>
                    </div>

                    {this.renderGeneralData()}
                    {OrderUtils.displayTemperatures(this.state.orderData!.relationType) &&this.renderTemperature()}
                    {this.renderTransportation()}
                    {OrderUtils.displayVoyageSection(this.state.orderData!.relationType) && this.renderVoyage()}
                    {this.renderCargo()}
                    {OrderUtils.displayContainerProperties(this.state.orderData!.relationType, this.state.orderData!.cargoType) && this.renderContainer()}
                    {OrderUtils.displayCostsSection(this.state.orderData!.relationType) && this.renderCosts()}
                    {this.renderDangerousGoods()}
                </div>
            </>
        );
    }

    private renderGeneralData() {
        return (
            <div className="section">
                <div className="section-header">
                    {i18n.t("transportation.order.details.general.header")}
                </div>
                <div className="section-content">
                    <SimpleInfoElement
                        label={i18n.t("transportation.order.details.general.properties.status")}
                        value={`${i18n.t(`orderStatus.${this.state.orderData!.status}`)}`}
                        testId="status"
                    />
                    <SimpleInfoElement
                        label={i18n.t("transportation.order.details.general.properties.relationType")}
                        value={`${i18n.t(`relationType.${this.state.orderData!.relationType}`)}`}
                        testId="relation-type"
                    />
                    <SimpleInfoElement
                        label={i18n.t("transportation.order.details.general.properties.cargoType")}
                        value={`${i18n.t(`cargoType.${this.state.orderData!.cargoType}`)}`}
                        testId="cargo-type"
                    />
                    {OrderUtils.displayPinCode(this.state.orderData!.relationType) && (
                        <SimpleInfoElement
                            label={i18n.t("transportation.order.details.general.properties.pinCode")}
                            value={this.state.orderData!.pinCode}
                            testId="pin-code"
                        />
                    )}
                    {OrderUtils.displayBarCode(this.state.orderData!.relationType) && (
                        <SimpleInfoElement
                            label={i18n.t("transportation.order.details.general.properties.barcodeNo")}
                            value={this.state.orderData!.barcodeNo}
                            testId="barcode-no"
                        />
                    )}
                    <SimpleInfoElement
                        label={i18n.t("transportation.order.details.general.properties.currentRoute")}
                        value={this.state.orderData!.currentRoute}
                        testId="current-route"
                    />
                    <SimpleInfoElement
                        label={i18n.t("transportation.order.details.general.properties.gateNo")}
                        value={this.state.orderData!.gateNo}
                        testId="gate-no"
                    />
                    <SimpleInfoElement
                        label={i18n.t("transportation.order.details.general.properties.createdAt")}
                        value={Utils.renderTime(this.state.orderData!.createdAt)}
                        testId="created-at"
                    />
                </div>
            </div>
        );
    }

    private renderTemperature() {
        return (
            <div className="section">
                <div className="section-header">
                    {i18n.t("transportation.order.details.temperature.header")}
                </div>
                <div className="section-content">
                    <SimpleInfoElement
                        label={i18n.t("transportation.order.details.temperature.properties.requestedTemp1")}
                        value={this.state.orderData!.requestedTemp1}
                        unit="°C"
                        testId="requested-temperature-one"
                    />
                    <SimpleInfoElement
                        label={i18n.t("transportation.order.details.temperature.properties.requestedTemp2")}
                        value={this.state.orderData!.requestedTemp2}
                        unit="°C"
                        testId="requested-temperature-two"
                    />
                </div>
            </div>
        );
    }

    private renderTransportation() {
        return (
            <div className="section">
                <div className="section-header">
                    {i18n.t("transportation.order.details.transportation.header")}
                </div>
                <div className="section-content">
                    {OrderUtils.displayTrailerRegNo(this.state.orderData!.relationType) && (
                        <SimpleInfoElement
                            label={i18n.t("transportation.order.details.transportation.properties.trailerRegNo")}
                            value={this.state.orderData!.trailerRegNo}
                            testId="trailer-reg-no"
                        />
                    )}
                    <SimpleInfoElement
                        label={i18n.t("transportation.order.details.transportation.properties.transportCompany")}
                        value={this.state.orderData!.transportCompany}
                        testId="transport-company"
                    />
                    {OrderUtils.displayRecipient(this.state.orderData!.relationType) && (
                        <SimpleInfoElement
                            label={i18n.t("transportation.order.details.transportation.properties.recipient")}
                            value={this.state.orderData!.recipient}
                            testId="recipient"
                        />
                    )}
                    {OrderUtils.displayLorryRegNo(this.state.orderData!.relationType) && (
                        <SimpleInfoElement
                            label={i18n.t("transportation.order.details.transportation.properties.lorryRegNo")}
                            value={this.state.orderData!.lorryRegNo}
                            testId="lorry-reg-no"
                        />
                    )}
                    {OrderUtils.displaySupplierLorryRegNo(this.state.orderData!.relationType) && (
                        <SimpleInfoElement
                            label={i18n.t("transportation.order.details.transportation.properties.supplierLorryRegNo")}
                            value={this.state.orderData!.supplierLorryRegNo}
                            testId="supplier-lorry-reg-no"
                        />
                    )}
                    {OrderUtils.displayRecipientLorryRegNo(this.state.orderData!.relationType) && (
                        <SimpleInfoElement
                            label={i18n.t("transportation.order.details.transportation.properties.recipientLorryRegNo")}
                            value={this.state.orderData!.recipientLorryRegNo}
                            testId="recipient-lorry-reg-no"
                        />
                    )}
                </div>
            </div>
        );
    }

    private renderVoyage() {
        return (
            <div className="section">
                <div className="section-header">
                    {i18n.t("transportation.order.details.voyage.header")}
                </div>
                <div className="section-content">
                    <SimpleInfoElement
                        label={i18n.t("transportation.order.details.voyage.properties.shipownerName")}
                        value={this.state.orderData!.shipownerName}
                        testId="shipowner-name"

                    />
                    <SimpleInfoElement
                        label={i18n.t("transportation.order.details.voyage.properties.voyageNo")}
                        value={this.state.orderData!.voyageNo}
                        testId="voyage-no"

                    />
                    <SimpleInfoElement
                        label={i18n.t("transportation.order.details.voyage.properties.arrivalDepartureTime")}
                        value={Utils.renderTime(this.state.orderData!.arrivalDepartureTime)}
                        testId="arrival-departure-time"
                    />
                    <SimpleInfoElement
                        label={i18n.t("transportation.order.details.voyage.properties.ferryName")}
                        value={this.state.orderData!.ferryName}
                        testId="ferry-name"
                    />
                    <SimpleInfoElement
                        label={i18n.t("transportation.order.details.voyage.properties.originDestinationPort")}
                        value={this.state.orderData!.originDestinationPort}
                        testId="origin-destination-port"
                    />
                    <SimpleInfoElement
                        label={i18n.t("transportation.order.details.voyage.properties.originDestinationCountry")}
                        value={this.state.orderData!.originDestinationCountry}
                        testId="origin-destination-country"
                    />
                    <SimpleInfoElement
                        label={i18n.t("transportation.order.details.voyage.properties.bookingNo")}
                        value={this.state.orderData!.bookingNo}
                        testId="booking-no"
                    />
                </div>
            </div>
        );
    }

    private renderCargo() {
        return (
            <div className="section">
                <div className="section-header">
                    {i18n.t("transportation.order.details.cargo.header")}
                </div>
                <div className="section-content">
                    <SimpleInfoElement
                        label={i18n.t("transportation.order.details.cargo.properties.cargoDesc")}
                        value={this.state.orderData!.cargoDesc}
                        testId="cargo-desc"
                    />
                    <SimpleInfoElement
                        label={i18n.t("transportation.order.details.cargo.properties.weight")}
                        value={this.state.orderData!.weight}
                        unit="kg"
                        testId="weight"
                    />
                    <SimpleInfoElement
                        label={i18n.t("transportation.order.details.cargo.properties.length")}
                        value={this.state.orderData!.length}
                        unit="m"
                        testId="length"
                    />
                    <SimpleInfoElement
                        label={i18n.t("transportation.order.details.cargo.properties.width")}
                        value={this.state.orderData!.width}
                        unit="m"
                        testId="width"
                    />
                    <SimpleInfoElement
                        label={i18n.t("transportation.order.details.cargo.properties.height")}
                        value={this.state.orderData!.height}
                        unit="m"
                        testId="height"
                    />
                    {OrderUtils.displayCargoProperties(this.state.orderData!.relationType) && (
                        <>
                            <SimpleInfoElement
                                label={i18n.t("transportation.order.details.cargo.properties.packingQuantity")}
                                value={this.state.orderData!.packingQuantity}
                                testId="packing-quantity"
                            />
                            <SimpleInfoElement
                                label={i18n.t("transportation.order.details.cargo.properties.cbm")}
                                value={this.state.orderData!.cbm}
                                testId="cbm"
                            />
                            <SimpleInfoElement
                                label={i18n.t("transportation.order.details.cargo.properties.packing")}
                                value={this.state.orderData!.packing}
                                testId="packing"
                            />
                            <SimpleInfoElement
                                label={i18n.t("transportation.order.details.cargo.properties.remarks")}
                                value={this.state.orderData!.remarks}
                                testId="remarks"
                            />
                        </>
                    )}
                    {OrderUtils.displayWagonRegNo(this.state.orderData!.relationType) && (
                        <SimpleInfoElement
                            label={i18n.t("transportation.order.details.cargo.properties.wagonRegNo")}
                            value={this.state.orderData!.wagonRegNo}
                            testId="wagon-reg-no"
                        />
                    )}
                    {OrderUtils.displaySupplierWagonRegNo(this.state.orderData!.relationType) && (
                        <SimpleInfoElement
                            label={i18n.t("transportation.order.details.cargo.properties.supplierWagonRegNo")}
                            value={this.state.orderData!.supplierWagonRegNo}
                            testId="supplier-wagon-reg-no"
                        />
                    )}
                    {OrderUtils.displayRecipientWagonRegNo(this.state.orderData!.relationType) && (
                        <SimpleInfoElement
                            label={i18n.t("transportation.order.details.cargo.properties.recipientWagonRegNo")}
                            value={this.state.orderData!.recipientWagonRegNo}
                            testId="recipient-wagon-reg-no"
                        />
                    )}
                    {OrderUtils.displaySecuringDeclaration(this.state.orderData!.relationType) && (
                        <SimpleInfoElement
                            label={i18n.t("transportation.order.details.cargo.properties.securingDeclaration")}
                            value={Utils.renderBooleanYesNoValue(this.state.orderData!.securingDeclaration, true)}
                            testId="securing-declaration"
                        />
                    )}
                </div>
            </div>
        );
    }

    private renderContainer() {
        return (
            <div className="section">
                <div className="section-header">
                    {i18n.t("transportation.order.details.container.header")}
                </div>
                <div className="section-content">
                    <SimpleInfoElement
                        label={i18n.t("transportation.order.details.container.properties.containerNo")}
                        value={this.state.orderData!.containerNo}
                        testId="container-no"
                    />
                    <SimpleInfoElement
                        label={i18n.t("transportation.order.details.container.properties.containerSize")}
                        value={getContainerSize(this.state.orderData!.containerSize)}
                        testId="container-size"
                    />
                    <SimpleInfoElement
                        label={i18n.t("transportation.order.details.container.properties.vgm")}
                        value={this.state.orderData!.vgm}
                        unit="kg"
                        testId="vgm"
                    />
                </div>
            </div>
        );
    }

    private renderCosts() {
        return (
            <div className="section">
                <div className="section-header">
                    {i18n.t("transportation.order.details.costs.header")}
                </div>
                <div className="section-content">
                    <SimpleInfoElement
                        label={i18n.t("transportation.order.details.costs.properties.storageTime")}
                        value={TransportUtils.generateTimeInHoursWithMinutesFormat(this.state.orderData!.storageTimeInMinutes, true)}
                        testId="storage-time"
                    />
                    <SimpleInfoElement
                        label={i18n.t("transportation.order.details.costs.properties.chargeableTime")}
                        value={TransportUtils.generateTimeInHoursWithMinutesFormat(this.state.orderData!.chargeableTimeInMinutes, true)}
                        testId="chargeable-time"
                    />
                    <SimpleInfoElement
                        label={i18n.t("transportation.order.details.costs.properties.parkingCost")}
                        value={this.state.orderData!.parkingCost}
                        unit="PLN"
                        testId="parking-cost"
                    />
                    <SimpleInfoElement
                        label={i18n.t("transportation.order.details.costs.properties.paymentMethod")}
                        value={this.renderPaymentMethod()}
                        testId="payment-method"
                    />
                    <SimpleInfoElement
                        label={i18n.t("transportation.order.details.costs.properties.paymentWaiver")}
                        value={OrderUtils.calculatePaymentWaiver(this.state.orderData!.parkingCost)}
                        testId="payment-waiver"
                    />
                    <SimpleInfoElement
                        label={i18n.t("transportation.order.details.costs.properties.paymentDocumentNumber")}
                        value={this.state.orderData!.paymentDocumentNumber}
                        testId="payment-document-number"
                    />
                </div>
            </div>
        );
    }

    private renderDangerousGoods() {
        const properties = ["unCode", "packing", "packingQuantity", "packingGroup", "weight"];
        const headers = properties.map(key => i18n.t("transportation.order.details.dangerousGoods.properties." + key));

        return (
            <div className="section">
                <div className="section-header">
                    {i18n.t("transportation.order.details.dangerousGoods.header")}
                </div>
                <Table
                    headers={headers}
                    rows={this.state.orderData!.dangerousGoods || []}
                    properties={properties}
                    renderCell={this.renderDangerousGoodsListCell.bind(this)}
                    testId="dangerous-goods"
                    numberOfElements={this.numberOfElementsForDangerousGood()}
                    currentPage={0}
                />
            </div>
        );
    }

    private renderDangerousGoodsListCell(row: any, property: string): JSX.Element {
        if (property === "unCode" && row.unCode && row.unCode.id && row.unCode.name) {
            return <>
                <span className="un-code-id">{row.unCode.id || ""}: </span>
                <span>{row.unCode.name || ""}</span>
            </>;
        }

        return row[property];
    }

    private renderPaymentMethod() {
        return this.state.orderData!.paymentMethod ? `${i18n.t(`paymentMethod.${this.state.orderData!.paymentMethod}`)}` : null;
    }

    private renderAllowOrNotAllowPopups() {
        const isAllowPopup = this.state.displayPopup === "allowPopup";

        return (
            <AllowOrNotAllowPopup
                popupType={this.state.displayPopup}
                orderType="order"
                closePopup={this.closePopup.bind(this)}
                confirm={this.confirm.bind(this,isAllowPopup ? "allowPopup" : "notAllowPopup", isAllowPopup)}
                response={isAllowPopup ? this.state.allowResponse : this.state.notAllowResponse}
                isOrderPopup
                orderId={this.state.orderData ? this.state.orderData!.id : undefined}
                onChangeDropdown={(newValue: any) => {
                    this.state.selectedLocation.setValue(newValue);
                }}
                selectedLocation={this.state.selectedLocation}
                isDownloadLocations
                isWaiting={this.state.isPopupActionWaiting}
            />
        );
    }

    private renderAssignWagonsNumberPopup() {
        return (
            <AssignWagonsNumberPopup
                displayPopup={this.state.displayPopup === "assignWagonsNumberPopup"}
                relationType={this.state.orderData!.relationType}
                closePopup={this.closePopup.bind(this)}
                confirm={this.confirm.bind(this, "assignWagonsNumberPopup", false)}
                response={this.state.assignWagonsNumberResponse}
                isWaiting={this.state.isPopupActionWaiting}
                wagonRegNo={this.state.wagonRegNo}
                supplierWagonRegNo={this.state.supplierWagonRegNo}
                recipientWagonRegNo={this.state.recipientWagonRegNo}
                orderData={this.state.orderData!}
            />
        );
    }

    private async reload() {
        this.setState({ orderData: undefined });
        const response = this.state.id ? await OrderService.getOrder(this.state.id!) : undefined;

        this.setState({
            orderData: Api.isSuccess(response) ? response?.data : null,
            response
        });
    }

    private numberOfElementsForDangerousGood() {
        if (this.state.orderData!.dangerousGoods && this.state.orderData!.dangerousGoods.length > 0) {
            return this.state.orderData!.dangerousGoods.length;
        } else return 0;
    }

    private onAllowOrNorAllowAction(displayPopup: "allowPopup" | "notAllowPopup" | null) {
        this.state.selectedLocation.setValue("");
        this.setState({ displayPopup });
    }

    private closePopup() {
        this.state.wagonRegNo.clearValidation();
        this.state.recipientWagonRegNo.clearValidation();
        this.state.supplierWagonRegNo.clearValidation();

        this.state.wagonRegNo.setValue("");
        this.state.recipientWagonRegNo.setValue("");
        this.state.supplierWagonRegNo.setValue("");

        this.setState({
            displayPopup: null,
            allowResponse: undefined,
            notAllowResponse: undefined,
            assignWagonsNumberResponse: undefined
        });
    }

    public get isValidSelectedLocation() {
        this.state.selectedLocation.validate();

        return this.state.selectedLocation.isValid;
    }

    public get isValidWagonNumbers() {
        this.state.wagonRegNo.validate();
        this.state.supplierWagonRegNo.validate();
        this.state.recipientWagonRegNo.validate();

        return this.state.wagonRegNo.isValid && this.state.supplierWagonRegNo.isValid && this.state.recipientWagonRegNo.isValid;
    }

    private async confirm(popupType: "allowPopup" | "notAllowPopup" | "assignWagonsNumberPopup", isAllow: boolean) {
        this.setState({
            isPopupActionWaiting: true,
            response: undefined,
            allowResponse: undefined,
            notAllowResponse: undefined,
            assignWagonsNumberResponse: undefined
        });
        let response = undefined;

        if (this.isValidSelectedLocation || this.isValidWagonNumbers) {
            if (popupType === "allowPopup" || popupType === "notAllowPopup") {
                response = await OrderService.allowOrNotAllowLocation(this.state.orderData!.id, this.state.selectedLocation.value, isAllow);

                if (isAllow) {
                    this.setState({ allowResponse: response });
                } else {
                    this.setState({ notAllowResponse: response });
                }
            } else if (popupType === "assignWagonsNumberPopup") {
                const wagonsNumber = {
                    wagonRegNo: this.state.wagonRegNo.value,
                    supplierWagonRegNo: this.state.supplierWagonRegNo.value,
                    recipientWagonRegNo: this.state.recipientWagonRegNo.value
                };

                response = await OrderService.assignWagonsNumber(this.state.orderData!.id, wagonsNumber);

                this.setState({ assignWagonsNumberResponse: response });
            }

            if (Api.isSuccess(response)) {
                const response = await OrderService.getOrder(this.state.orderData!.id);

                this.setState({
                    orderData: response?.data || null,
                    response
                });

                this.closePopup();
            }
        }

        Utils.scrollToFirstErrorMessage();
        this.setState({isPopupActionWaiting: false});
    }
}

export default withRouter(observer(OrderDetails));
